import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Chip,
  Paper,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EmailIcon from '@mui/icons-material/Email';
import LinkIcon from '@mui/icons-material/Link';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { withTranslation } from 'react-i18next';
import axios from 'axios';

const actionIcons = {
  imported_email: <EmailIcon fontSize="small" />,
  declined_email: <CancelIcon fontSize="small" />,
  imported_url: <LinkIcon fontSize="small" />,
  imported_file: <UploadFileIcon fontSize="small" />,
  imported_clipboard: <ContentPasteIcon fontSize="small" />,
  added_manually: <EditIcon fontSize="small" />,
  added_to_maintenance: <AddIcon fontSize="small" />,
  removed_from_maintenance: <RemoveIcon fontSize="small" />,
};

const actionColors = {
  imported_email: 'success',
  declined_email: 'error',
  imported_url: 'success',
  imported_file: 'success',
  imported_clipboard: 'success',
  added_manually: 'info',
  added_to_maintenance: 'primary',
  removed_from_maintenance: 'warning',
};

const PartAuditLogDialog = ({ t, open, onClose }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      fetchLogs();
    }
  }, [open]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/parts/audit-log/');
      setLogs(response.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fi-FI', {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return '';
    return new Intl.NumberFormat('fi-FI', {
      style: 'currency',
      currency: 'EUR',
    }).format(price);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          maxHeight: '80vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: '18px' }}>
          {t('Part Activity Log')}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" sx={{ textAlign: 'center', py: 4 }}>
            {error}
          </Typography>
        ) : logs.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              {t('No activity recorded yet')}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {logs.map((log) => (
              <Paper
                key={log.id}
                elevation={0}
                sx={{
                  p: 2,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: '10px',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: '8px',
                      bgcolor: `${actionColors[log.action]}.lighter`,
                      color: `${actionColors[log.action]}.main`,
                    }}
                  >
                    {actionIcons[log.action] || <CheckCircleIcon fontSize="small" />}
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography sx={{ fontWeight: 500 }}>
                        {log.part_name || t('Unknown Part')}
                      </Typography>
                      {log.part_price && (
                        <Typography variant="body2" color="text.secondary">
                          {formatPrice(log.part_price)}
                        </Typography>
                      )}
                    </Box>

                    <Chip
                      label={log.action_display}
                      size="small"
                      color={actionColors[log.action] || 'default'}
                      variant="outlined"
                      sx={{ mb: 1 }}
                    />

                    {log.source_reference && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 0.5,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '500px',
                        }}
                      >
                        {log.source_reference}
                      </Typography>
                    )}

                    {log.maintenance_number && (
                      <Typography variant="body2" color="text.secondary">
                        {t('Maintenance')}: {log.maintenance_number}
                      </Typography>
                    )}

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                      <Typography variant="caption" color="text.disabled">
                        {formatDate(log.created_at)}
                      </Typography>
                      {log.user_name && (
                        <Typography variant="caption" color="text.disabled">
                          {log.user_name}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default withTranslation()(PartAuditLogDialog);
